import React, { useMemo, useCallback } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

import {
  CtaButton,
  EmptySectionMessage,
  FullPageLoader,
} from '@tassomutuo-backoffice/ui';
import {
  getLeadsUnderManagement,
  needUpdateNotifyMany,
} from '../../services/api/lead/lead';
import LeadCardList from './components/LeadCardList';
import LeadFilterSection from './components/LeadFilterSection';
import LeadsUnderManagementActionSummary from './components/LeadsUnderManagementActionSummary';
import useLeadsFilterAndOrder from './hooks/useLeadsFilterAndOrder';
import useLeadActionRequired, {
  ActionRequiredType,
} from './hooks/useLeadActionRequired';
import { LeadProductChip } from '../../components';

// Mapping tra action type e label per la visualizzazione
type NonNullActionType = Exclude<ActionRequiredType, null>;
const ACTION_FILTER_LABELS: Record<NonNullActionType, string> = {
  appointmentToBeRescheduled: 'Appuntamenti da rifissare',
  oldUpdate: 'Lead non aggiornate',
  appointmentHasPassed: 'Lead da aggiornare',
  appointmentToBeConfirmed: 'Appuntamenti da confermare',
};

const LeadsUnderManagementPage = () => {
  const { renderActionRequired } = useLeadActionRequired();
  const { orderLeadByActionRequired } = useLeadsFilterAndOrder();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isPending, data, error } = useQuery({
    queryKey: ['leadsUnderManagement'],
    queryFn: getLeadsUnderManagement,
  });

  const leadsUnderManagement = data?.results || [];

  // Leggi i filtri dall'URL
  const actionFilter = searchParams.get('action') as ActionRequiredType | null;
  const statusFilter = searchParams.get('status');
  const searchFilter = searchParams.get('search') || '';

  // Calcola le lead filtrate basandosi sui parametri URL
  const leadsToShow = useMemo(() => {
    if (!leadsUnderManagement.length) return [];

    let filtered = [...leadsUnderManagement];

    // Applica filtro action (quick filter)
    if (actionFilter && ACTION_FILTER_LABELS[actionFilter]) {
      filtered = filtered.filter(
        (lead) => renderActionRequired({ lead }, true) === actionFilter
      );
    }

    // Applica filtro status (deal stage)
    if (statusFilter) {
      const statuses = statusFilter.split(',');
      filtered = filtered.filter((lead) =>
        statuses.includes(lead.properties.dealstage || '')
      );
    }

    // Applica filtro ricerca testuale
    if (searchFilter) {
      const searchLower = searchFilter.toLowerCase();
      filtered = filtered.filter((lead) =>
        lead.properties.dealname.toLowerCase().includes(searchLower)
      );
    }

    // Se nessun filtro applicato, ordina per action required
    if (!actionFilter && !statusFilter && !searchFilter) {
      return orderLeadByActionRequired(filtered);
    }

    return filtered;
  }, [leadsUnderManagement, actionFilter, statusFilter, searchFilter, renderActionRequired, orderLeadByActionRequired]);

  // Label del filtro applicato per la UI
  const appliedFilterLabel = useMemo(() => {
    if (actionFilter && ACTION_FILTER_LABELS[actionFilter]) {
      return ACTION_FILTER_LABELS[actionFilter];
    }
    if (statusFilter) {
      const count = statusFilter.split(',').length;
      return `${count} stato/i selezionato/i`;
    }
    return null;
  }, [actionFilter, statusFilter]);

  const onResetAppliedFilter = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const onLeadActionCounterClick = useCallback((type: ActionRequiredType) => {
    if (!type) return;
    // Imposta solo il filtro action, rimuovendo gli altri
    setSearchParams({ action: type });
  }, [setSearchParams]);

  return (
    <>
      <FullPageLoader show={isPending} />
      <Box>
        <Box px={4} pt={4} pb={2} gap={1} display="flex" alignItems="center" flexWrap="wrap">
          <HourglassTopIcon fontSize="large" />
          <Typography variant="h4" fontWeight="bold">
            Lead in gestione
          </Typography>
          <LeadProductChip product="mutui" />
        </Box>

        {!isPending && error && (
          <Alert severity="error">
            {error.message ? `Error: ${error.message}` : 'An Error occurred'}
          </Alert>
        )}
        {leadsUnderManagement && leadsUnderManagement?.length > 0 && (
          <>
            <LeadsUnderManagementActionSummary
              leadsUnderManagement={leadsUnderManagement}
              onLeadActionCounterClick={onLeadActionCounterClick}
            />
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
            >
              {appliedFilterLabel ? (
                <Box display="flex" gap={1} flexWrap="wrap" px={4}>
                  <Typography variant="h6" fontWeight="bold">
                    Filtri applicati:
                  </Typography>
                  <Typography variant="h6">{appliedFilterLabel}</Typography>
                  <CtaButton variant="contained" onClick={onResetAppliedFilter}>
                    Resetta filtri
                  </CtaButton>

                  <CtaButton
                    sx={{
                      bgcolor: (th) => th.palette.primary.dark,
                      maxWidth: '140px',
                    }}
                    variant="contained"
                    onClick={() => needUpdateNotifyMany(leadsToShow)}
                  >
                    <MailOutlineIcon />
                    <Typography fontWeight="bold" pl={1}>
                      Notifica
                    </Typography>
                  </CtaButton>
                </Box>
              ) : (
                <Box display="flex" gap={1} flexWrap="wrap" px={4}>
                  <CtaButton
                    sx={{
                      bgcolor: (th) => th.palette.primary.dark,
                      maxWidth: '140px',
                    }}
                    variant="contained"
                    onClick={() => needUpdateNotifyMany(leadsToShow)}
                  >
                    <MailOutlineIcon />
                    <Typography fontWeight="bold" pl={1}>
                      Notifica
                    </Typography>
                  </CtaButton>
                </Box>
              )}
              <LeadFilterSection
                leads={leadsUnderManagement}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                showFilters={true}
              />
            </Box>
            {leadsToShow.map((lead) => (
              <LeadCardList key={lead.id} lead={lead} />
            ))}
          </>
        )}
        {leadsUnderManagement && leadsUnderManagement?.length === 0 && (
          <EmptySectionMessage title="Non ci sono lead da mostrare" />
        )}
        {leadsUnderManagement &&
          leadsUnderManagement?.length > 0 &&
          leadsToShow &&
          leadsToShow.length === 0 && (
            <EmptySectionMessage title="La ricerca non ha prodotto risultati" />
          )}
      </Box>
    </>
  );
};

export default LeadsUnderManagementPage;
